import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { 
  Loader2, 
  Plus, 
  Edit2, 
  Trash2, 
  FileText, 
  Building, 
  DoorOpen,
  Shield,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Check,
  X
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type TermsOfUse, type ActivityType, type Building as BuildingType, type Room } from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'
import { RichTextEditor, RichTextContent } from '../components/RichTextEditor'

interface TermsFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: { scope: 'organisation' | 'building' | 'room'; scopeId?: string; title: string; content: string }) => Promise<void>
  editingTerms?: TermsOfUse | null
  buildings: BuildingType[]
  rooms: Room[]
}

function TermsFormModal({ isOpen, onClose, onSave, editingTerms, buildings, rooms }: TermsFormModalProps) {
  const [scope, setScope] = useState<'organisation' | 'building' | 'room'>(editingTerms?.scope || 'organisation')
  const [scopeId, setScopeId] = useState(editingTerms?.scopeId || '')
  const [title, setTitle] = useState(editingTerms?.title || 'Terms of Use')
  const [content, setContent] = useState(editingTerms?.content || '')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (editingTerms) {
      setScope(editingTerms.scope)
      setScopeId(editingTerms.scopeId || '')
      setTitle(editingTerms.title)
      setContent(editingTerms.content)
    } else {
      setScope('organisation')
      setScopeId('')
      setTitle('Terms of Use')
      setContent('')
    }
  }, [editingTerms, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (!title.trim()) {
      setError('Title is required')
      return
    }
    if (!content.trim() || content === '<p><br></p>') {
      setError('Content is required')
      return
    }
    if (scope !== 'organisation' && !scopeId) {
      setError(`Please select a ${scope}`)
      return
    }

    setIsSaving(true)
    try {
      await onSave({
        scope,
        scopeId: scope === 'organisation' ? undefined : scopeId,
        title: title.trim(),
        content,
      })
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save terms')
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingTerms ? 'Edit Terms of Use' : 'Create Terms of Use'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scope
              </label>
              <select
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as 'organisation' | 'building' | 'room')
                  setScopeId('')
                }}
                disabled={!!editingTerms}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
              >
                <option value="organisation">Organisation-wide</option>
                <option value="building">Building-specific</option>
                <option value="room">Room-specific</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {scope === 'organisation' && 'These terms apply to all bookings in your organisation'}
                {scope === 'building' && 'These terms apply only to rooms in the selected building'}
                {scope === 'room' && 'These terms apply only to the selected room'}
              </p>
            </div>

            {scope === 'building' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building
                </label>
                <select
                  value={scopeId}
                  onChange={(e) => setScopeId(e.target.value)}
                  disabled={!!editingTerms}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                >
                  <option value="">Select a building...</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}

            {scope === 'room' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room
                </label>
                <select
                  value={scopeId}
                  onChange={(e) => setScopeId(e.target.value)}
                  disabled={!!editingTerms}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                >
                  <option value="">Select a room...</option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Terms of Use"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content
            </label>
            <RichTextEditor
              content={content}
              onChange={setContent}
              placeholder="Enter the terms of use content..."
            />
            {editingTerms && (
              <p className="mt-2 text-xs text-amber-600">
                ⚠️ Editing content will create a new version. Previous bookings retain the terms they accepted.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {editingTerms ? 'Update Terms' : 'Create Terms'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

interface ActivityTypeFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: { name: string; description?: string; requiresPli: boolean; sortOrder?: number }) => Promise<void>
  editingType?: ActivityType | null
}

function ActivityTypeFormModal({ isOpen, onClose, onSave, editingType }: ActivityTypeFormModalProps) {
  const [name, setName] = useState(editingType?.name || '')
  const [description, setDescription] = useState(editingType?.description || '')
  const [requiresPli, setRequiresPli] = useState(editingType?.requiresPli || false)
  const [sortOrder, setSortOrder] = useState(editingType?.sortOrder || 0)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (editingType) {
      setName(editingType.name)
      setDescription(editingType.description || '')
      setRequiresPli(editingType.requiresPli)
      setSortOrder(editingType.sortOrder)
    } else {
      setName('')
      setDescription('')
      setRequiresPli(false)
      setSortOrder(0)
    }
  }, [editingType, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    setIsSaving(true)
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        requiresPli,
        sortOrder,
      })
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save activity type')
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingType ? 'Edit Activity Type' : 'Create Activity Type'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Martial Arts, Book Club, Yoga"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description of this activity type"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <input
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">Lower numbers appear first</p>
          </div>

          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <input
              id="requiresPli"
              type="checkbox"
              checked={requiresPli}
              onChange={(e) => setRequiresPli(e.target.checked)}
              className="w-4 h-4 mt-0.5 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
            />
            <div>
              <label htmlFor="requiresPli" className="text-sm font-medium text-gray-900">
                Requires Public Liability Insurance
              </label>
              <p className="text-xs text-gray-600 mt-0.5">
                When checked, guests booking this activity type must provide a valid PLI certificate
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {editingType ? 'Update' : 'Create'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function SettingsTermsPage() {
  const navigate = useNavigate()
  const { currentOrg, isAuthenticated, isLoading: authLoading, checkAuth } = useAuthStore()
  
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Data
  const [terms, setTerms] = useState<TermsOfUse[]>([])
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([])
  const [buildings, setBuildings] = useState<BuildingType[]>([])
  const [rooms, setRooms] = useState<Room[]>([])
  
  // UI state
  const [activeTab, setActiveTab] = useState<'terms' | 'activities'>('terms')
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showActivityModal, setShowActivityModal] = useState(false)
  const [editingTerms, setEditingTerms] = useState<TermsOfUse | null>(null)
  const [editingActivity, setEditingActivity] = useState<ActivityType | null>(null)
  const [expandedTerms, setExpandedTerms] = useState<Set<string>>(new Set())
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login', search: {} })
    }
  }, [authLoading, isAuthenticated, navigate])

  useEffect(() => {
    if (!authLoading && isAuthenticated && currentOrg?.role !== 'admin') {
      navigate({ to: '/dashboard', search: {} })
    }
  }, [authLoading, isAuthenticated, currentOrg, navigate])

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadData()
    }
  }, [authLoading, isAuthenticated])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const [termsData, activityData, buildingsData, roomsData] = await Promise.all([
        api.terms.list(),
        api.terms.listActivityTypes(true),
        api.buildings.list(),
        api.rooms.list(),
      ])
      
      setTerms(termsData.terms)
      setActivityTypes(activityData.activityTypes)
      setBuildings(buildingsData.buildings)
      setRooms(roomsData.rooms)
    } catch (err: any) {
      setError(err.message || 'Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveTerms = async (data: { scope: 'organisation' | 'building' | 'room'; scopeId?: string; title: string; content: string }) => {
    if (editingTerms) {
      await api.terms.update(editingTerms.id, {
        title: data.title,
        content: data.content,
      })
      setSuccess('Terms updated successfully')
    } else {
      await api.terms.create(data)
      setSuccess('Terms created successfully')
    }
    setEditingTerms(null)
    loadData()
  }

  const handleSaveActivityType = async (data: { name: string; description?: string; requiresPli: boolean; sortOrder?: number }) => {
    if (editingActivity) {
      await api.terms.updateActivityType(editingActivity.id, data)
      setSuccess('Activity type updated successfully')
    } else {
      await api.terms.createActivityType(data)
      setSuccess('Activity type created successfully')
    }
    setEditingActivity(null)
    loadData()
  }

  const handleDeleteTerms = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate these terms? Existing bookings will retain their accepted terms.')) {
      return
    }
    
    setDeletingId(id)
    try {
      await api.terms.delete(id)
      setSuccess('Terms deactivated successfully')
      loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to delete terms')
    } finally {
      setDeletingId(null)
    }
  }

  const handleDeleteActivityType = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this activity type?')) {
      return
    }
    
    setDeletingId(id)
    try {
      await api.terms.deleteActivityType(id)
      setSuccess('Activity type deactivated successfully')
      loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to delete activity type')
    } finally {
      setDeletingId(null)
    }
  }

  const toggleTermsExpanded = (id: string) => {
    setExpandedTerms(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const getScopeName = (terms: TermsOfUse): string => {
    if (terms.scope === 'organisation') return 'Organisation-wide'
    if (terms.scope === 'building') {
      const building = buildings.find(b => b.id === terms.scopeId)
      return building ? `Building: ${building.name}` : 'Building (deleted)'
    }
    if (terms.scope === 'room') {
      const room = rooms.find(r => r.id === terms.scopeId)
      return room ? `Room: ${room.name}` : 'Room (deleted)'
    }
    return terms.scope
  }

  const getScopeIcon = (scope: string) => {
    switch (scope) {
      case 'building': return <Building className="w-4 h-4" />
      case 'room': return <DoorOpen className="w-4 h-4" />
      default: return <FileText className="w-4 h-4" />
    }
  }

  if (authLoading || isLoading) {
    return (
      <DashboardLayout title="Terms & Activity Types">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </DashboardLayout>
    )
  }

  const isAdmin = currentOrg?.role === 'admin'
  const activeTerms = terms.filter(t => t.isActive)
  const inactiveTerms = terms.filter(t => !t.isActive)
  const activeActivityTypes = activityTypes.filter(a => a.isActive)
  const inactiveActivityTypes = activityTypes.filter(a => !a.isActive)

  return (
    <DashboardLayout title="Terms & Activity Types">
      <div className="max-w-5xl">
        {/* Success/Error messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </span>
            <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              {success}
            </span>
            <button onClick={() => setSuccess('')} className="text-green-700 hover:text-green-900">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('terms')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'terms'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Terms of Use
              </span>
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'activities'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Activity Types & PLI
              </span>
            </button>
          </nav>
        </div>

        {/* Terms Tab */}
        {activeTab === 'terms' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Terms of Use</h2>
                <p className="text-sm text-gray-500">
                  Create terms that guests must accept when booking. Terms can be set at organisation, building, or room level.
                </p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => {
                    setEditingTerms(null)
                    setShowTermsModal(true)
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Terms
                </button>
              )}
            </div>

            {/* Active Terms */}
            {activeTerms.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No terms of use configured</p>
                <p className="text-sm text-gray-400 mt-1">
                  Add terms that guests must accept when making bookings
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeTerms.map((t) => (
                  <div key={t.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleTermsExpanded(t.id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedTerms.has(t.id) ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                        <div className="flex items-center gap-2 text-gray-500">
                          {getScopeIcon(t.scope)}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{t.title}</div>
                          <div className="text-sm text-gray-500">{getScopeName(t)}</div>
                        </div>
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                          v{t.version}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setEditingTerms(t)
                                setShowTermsModal(true)
                              }}
                              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteTerms(t.id)
                              }}
                              disabled={deletingId === t.id}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                              title="Deactivate"
                            >
                              {deletingId === t.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {expandedTerms.has(t.id) && (
                      <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                        <RichTextContent content={t.content} className="text-sm text-gray-700" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Inactive Terms */}
            {inactiveTerms.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Inactive Terms (Historical)</h3>
                <div className="space-y-2 opacity-60">
                  {inactiveTerms.map((t) => (
                    <div key={t.id} className="bg-gray-50 rounded-lg border border-gray-200 p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-gray-400">
                          {getScopeIcon(t.scope)}
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">{t.title}</div>
                          <div className="text-xs text-gray-400">{getScopeName(t)} • v{t.version}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Activity Types Tab */}
        {activeTab === 'activities' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Activity Types</h2>
                <p className="text-sm text-gray-500">
                  Define activity types that guests can select when booking. Mark activities that require Public Liability Insurance.
                </p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => {
                    setEditingActivity(null)
                    setShowActivityModal(true)
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Activity Type
                </button>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-900">About PLI Requirements</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Activities marked as requiring Public Liability Insurance will prompt guests to upload a valid certificate during the booking process. 
                    This is for <strong>your records only</strong> — Roomsi does not assess insurance adequacy.
                  </p>
                </div>
              </div>
            </div>

            {/* Active Activity Types */}
            {activeActivityTypes.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No activity types configured</p>
                <p className="text-sm text-gray-400 mt-1">
                  Add activity types that guests can select when booking
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">PLI Required</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Order</th>
                      {isAdmin && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {activeActivityTypes.map((a) => (
                      <tr key={a.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{a.name}</div>
                          {a.description && (
                            <div className="text-sm text-gray-500">{a.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {a.requiresPli ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              <Shield className="w-3 h-3" />
                              Required
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">No</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-500">
                          {a.sortOrder}
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => {
                                  setEditingActivity(a)
                                  setShowActivityModal(true)
                                }}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteActivityType(a.id)}
                                disabled={deletingId === a.id}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                                title="Deactivate"
                              >
                                {deletingId === a.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Inactive Activity Types */}
            {inactiveActivityTypes.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Inactive Activity Types</h3>
                <div className="space-y-2 opacity-60">
                  {inactiveActivityTypes.map((a) => (
                    <div key={a.id} className="bg-gray-50 rounded-lg border border-gray-200 p-3 flex items-center justify-between">
                      <div className="text-sm text-gray-600">{a.name}</div>
                      {a.requiresPli && (
                        <span className="text-xs text-gray-400">PLI was required</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modals */}
        <TermsFormModal
          isOpen={showTermsModal}
          onClose={() => {
            setShowTermsModal(false)
            setEditingTerms(null)
          }}
          onSave={handleSaveTerms}
          editingTerms={editingTerms}
          buildings={buildings}
          rooms={rooms}
        />

        <ActivityTypeFormModal
          isOpen={showActivityModal}
          onClose={() => {
            setShowActivityModal(false)
            setEditingActivity(null)
          }}
          onSave={handleSaveActivityType}
          editingType={editingActivity}
        />
      </div>
    </DashboardLayout>
  )
}
